import { Principal } from "@icp-sdk/core/principal";
import { sendToken, type TransferTokenId } from "@motokomaker/ic-client";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { TokenLabel } from "@/components/brand/TokenAmount";
import { ConfirmSheet } from "@/components/defi/ConfirmSheet";
import { DemoFaucetPanel } from "@/components/wallet/DemoFaucetPanel";
import { spendableTokenMax } from "@/lib/amount-limits";
import { useAuth } from "@/lib/auth";
import { recordClientTransfer } from "@/lib/client-activity";
import { copyToClipboard } from "@/lib/clipboard";
import { getConfig } from "@/lib/protocol-actor";
import { formatAmountPlain, formatNum } from "@/lib/utils";
import type { ExtendedTokenId } from "@/lib/token-assets";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const SENDABLE = new Set([
	"USDM",
	"ICP",
	"ckBTC",
	"ckETH",
	"MMK",
	"ckUSDC",
]);

export function WalletAssetSheet({
	mode,
	token,
	open,
	onOpenChange,
	balance,
}: {
	mode: "deposit" | "send" | null;
	token: ExtendedTokenId;
	open: boolean;
	onOpenChange: (o: boolean) => void;
	balance: number;
}) {
	const {
		principal,
		refreshBalances,
		ensureProtocolActor,
		reconnectProtocol,
	} = useAuth();
	const qc = useQueryClient();
	const [to, setTo] = useState("");
	const [amount, setAmount] = useState("");
	const [sending, setSending] = useState(false);

	const spendable = spendableTokenMax(balance);
	const dec = token === "ckBTC" ? 6 : token === "MMK" ? 0 : 4;

	const handleSend = useCallback(async () => {
		if (!SENDABLE.has(token)) {
			toast.error(`Cannot send ${token}`);
			return;
		}
		const dest = to.trim();
		if (!dest) {
			toast.error("Enter a destination principal");
			return;
		}
		let toPrincipal: Principal;
		try {
			toPrincipal = Principal.fromText(dest);
		} catch {
			toast.error("Invalid destination principal");
			return;
		}
		const amt = Number(amount);
		if (!Number.isFinite(amt) || amt <= 0) {
			toast.error("Enter a positive amount");
			return;
		}
		if (amt > spendable + 1e-12) {
			toast.error(
				`Insufficient ${token} (spendable ${spendable} after fee; wallet ${balance})`,
			);
			return;
		}
		setSending(true);
		try {
			const pair = await ensureProtocolActor();
			if (!pair) {
				toast.error("Not connected to protocol — Connect or Reconnect");
				void reconnectProtocol();
				return;
			}
			const config = getConfig();
			const block = await sendToken(
				pair.actor,
				pair.agent,
				config,
				token as TransferTokenId,
				toPrincipal,
				amt,
			);
			if (principal) {
				recordClientTransfer({
					principal,
					asset: token,
					amount: amt,
					direction: "out",
					counterparty: dest,
					txRef: block.toString(),
					detail: `Sent ${token} (block ${block.toString()})`,
				});
			}
			toast.success(`Sent ${amt} ${token} (block ${block.toString()})`);
			setAmount("");
			await refreshBalances();
			void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
			onOpenChange(false);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : String(err ?? "Transfer failed"),
			);
		} finally {
			setSending(false);
		}
	}, [
		token,
		to,
		amount,
		spendable,
		balance,
		ensureProtocolActor,
		reconnectProtocol,
		principal,
		refreshBalances,
		qc,
		onOpenChange,
	]);

	const copyPrincipal = async () => {
		if (!principal) {
			toast.error("Connect first");
			return;
		}
		const ok = await copyToClipboard(principal);
		if (ok) toast.success("Principal copied");
		else toast.error("Copy failed");
	};

	if (!mode) return null;

	return (
		<ConfirmSheet
			open={open}
			onOpenChange={onOpenChange}
			title={mode === "deposit" ? `Deposit ${token}` : `Send ${token}`}
			description={
				mode === "deposit"
					? `Receive ${token} to your Internet Identity principal.`
					: `Transfer ${token} to another principal (fee reserved on Max).`
			}
			confirmLabel={
				mode === "deposit"
					? "Done"
					: sending
						? "Sending…"
						: `Send ${token}`
			}
			onConfirm={() => {
				if (mode === "deposit") onOpenChange(false);
				else void handleSend();
			}}
			isPending={sending}
			confirmDisabled={
				mode === "send" &&
				(sending || !amount || Number(amount) <= 0 || !to.trim())
			}
		>
			<div className="space-y-4">
				<div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-3 py-3">
					<TokenIcon token={token} size="lg" plain />
					<div>
						<p className="text-sm font-bold">{token}</p>
						<p className="font-mono-amount text-xs text-muted-foreground">
							Balance {formatNum(balance, dec)}
							{mode === "send"
								? ` · spendable ${formatNum(spendable, dec)}`
								: ""}
						</p>
					</div>
				</div>

				{mode === "deposit" ? (
					<>
						{token === "USDM" || token === "ICP" || token === "ckBTC" ? (
							<DemoFaucetPanel />
						) : null}
						<div className="rounded-xl border border-border bg-background p-3">
							<p className="text-[10px] uppercase text-muted-foreground">
								Your principal
							</p>
							<p className="mt-1 break-all font-mono text-xs">
								{principal ?? "Connect to reveal"}
							</p>
						</div>
						<button
							type="button"
							className="h-11 w-full rounded-xl border border-border text-sm font-semibold"
							disabled={!principal}
							onClick={() => void copyPrincipal()}
						>
							Copy address
						</button>
						<p className="text-[11px] text-muted-foreground">
							Vault deposits, mints, and repays appear under Activity after
							they confirm on-chain.
						</p>
					</>
				) : (
					<>
						<label className="block">
							<span className="text-xs font-semibold text-muted-foreground">
								Destination principal
							</span>
							<input
								value={to}
								onChange={(e) => setTo(e.target.value)}
								placeholder="aaaaa-..."
								className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 font-mono text-sm"
							/>
						</label>
						<label className="block">
							<span className="text-xs font-semibold text-muted-foreground">
								Amount
							</span>
							<div className="mt-1 flex items-center gap-2">
								<input
									value={amount}
									onChange={(e) => setAmount(e.target.value)}
									placeholder="0.00"
									inputMode="decimal"
									className="h-11 min-w-0 flex-1 rounded-xl border border-border bg-background px-3 font-mono-amount text-sm"
								/>
								<TokenLabel id={token} size="sm" />
							</div>
							<div className="mt-1.5 flex gap-2">
								<button
									type="button"
									className="text-[11px] font-semibold text-primary"
									onClick={() =>
										setAmount(formatAmountPlain(spendable / 2, dec))
									}
								>
									50%
								</button>
								<button
									type="button"
									className="text-[11px] font-semibold text-primary"
									onClick={() =>
										setAmount(formatAmountPlain(spendable, dec))
									}
								>
									Max (after fee)
								</button>
							</div>
						</label>
					</>
				)}
			</div>
		</ConfirmSheet>
	);
}
